import { VaultsListCard } from '@/components/shared/vaultsListCard'
import { useFilteredVaults } from '@/hooks/useFilteredVaults'
import { useSortedVaults } from '@/hooks/useSortedVaults'
import { useVaultsSearchState } from '@/hooks/useVaultsSearchState'
import { useVaultsSortState } from '@/hooks/useVaultsSortState'
import type { Vault } from '@/types/vault'
import { cn } from '@/utils/cn'
import { useVirtualizer } from '@tanstack/react-virtual'
import { useCallback, useRef } from 'react'
import { Card } from '../common/card'
import { ScrollArea, ScrollBar } from '../common/scrollArea'
import { SearchIcon } from '../icons/search'
import { Content } from '../layout/wrappers'
import { SearchFilter } from '../shared/searchFilter'
import { DiscoverNavigation } from './discoverNavigation'

interface Props {
  vaults: Vault[]
  className?: string
}

export const VaultsCards = ({ vaults, className }: Props) => {
  const virtualizerRef = useRef<HTMLDivElement>(null)

  const {
    searchFor,
    selectedNetworks,
    selectedTokens,
    selectedProtocols,
    selectedCurators,
    selectedTags,
    selectedTvlRange,
    includeRewards,
    apyRange,
    onlyTransactional,
    minVaultScore,
  } = useVaultsSearchState()
  const { sortState } = useVaultsSortState()
  const filteredVaults = useFilteredVaults({
    vaults,
    searchFor,
    selectedNetworks,
    selectedTokens,
    selectedProtocols,
    selectedTags,
    selectedCurators,
    selectedTvlRange,
    onlyTransactional,
    minVaultScore,
  })
  const sortedVaults = useSortedVaults({ vaults: filteredVaults, apyRange, includeRewards, ...sortState })
  const getItemKey = useCallback(
    (index: number) => {
      const vault = sortedVaults[index]
      return `${vault.network.name}:${vault.vaultId}`
    },
    [sortedVaults]
  )

  const virtualizer = useVirtualizer({
    count: sortedVaults.length,
    getScrollElement: () => virtualizerRef.current,
    getItemKey,
    estimateSize: () => 166,
    overscan: 5,
  })

  return (
    <ScrollArea viewportRef={virtualizerRef} className={cn('h-full', className)}>
      <Content>
        <section className="flex flex-col gap-4">
          <DiscoverNavigation activePage="vaults" />
          <SearchFilter
            numberOfVaults={sortedVaults.length}
            className="md:hidden [&>input]:bg-surface"
            trackingParams={{ Page: 'Discover' }}
          />
          {sortedVaults.length === 0 ? (
            <Card className="flex w-full flex-col items-center justify-center gap-4 py-4">
              <SearchIcon size={48} />
              <p className="font-bold text-xl">No results for selected filters</p>
            </Card>
          ) : (
            <div
              style={{
                height: `${virtualizer.getTotalSize()}px`,
                position: 'relative',
                width: '100%',
              }}
            >
              {virtualizer.getVirtualItems().map((virtualItem) => {
                const vault = sortedVaults[virtualItem.index]
                return (
                  <div
                    key={virtualItem.key}
                    data-index={virtualItem.index}
                    ref={virtualizer.measureElement}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      transform: `translateY(${virtualItem.start}px)`,
                    }}
                    className="pb-2"
                  >
                    <VaultsListCard vault={vault} apyRange={apyRange} includeRewards={includeRewards} />
                  </div>
                )
              })}
            </div>
          )}
        </section>
      </Content>
      <ScrollBar orientation="vertical" />
    </ScrollArea>
  )
}
